import { useMutation } from '@tanstack/react-query';

// --- Configuration ---
const API_BASE = import.meta.env.VITE_AUTH_DOMAIN || '';

// 1. Generic Submission Fetcher Function (The 'mutationFn')
// It receives the submissionEndpoint from the custom hook closure.
const submitGeneric = async ({ payload, csrfToken, submissionEndpoint }) => {

  // Check if endpoint is provided
  if (!submissionEndpoint) {
    throw new Error('Submission endpoint is missing.');
  }

  const response = await fetch(submissionEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Only send the CSRF header when a real token exists. `csrfToken` is
      // commonly null/undefined (CSRF_COOKIE_HTTPONLY=True hides it from JS and
      // these endpoints are JWT + reCAPTCHA gated, not CSRF-protected). Setting
      // it unconditionally would make fetch send the literal string "null".
      ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    const error = new Error(errorData.error || errorData.message || 'Submission failed due to server error.');
    // Attach the DRF field-specific errors object to the Error instance
    error.errors = errorData.errors;
    throw error;
  }

  return response.json();
};

/**
 * Custom hook for handling generic form submissions via POST request.
 * @param {string} endpointPath - The unique API path (e.g., 'api/v1/contacts/demo/submission/').
 * @param {Function} setRecaptchaError - State setter for reCAPTCHA error.
 * @param {Function} setSubmissionError - State setter for general submission error message.
 * @param {Function} setCurrentStep - State setter for navigation step (e.g., to show success screen).
 */
export const useGenericSubmissionMutation = ({
  endpointPath,
  setRecaptchaError,
  setSubmissionError,
  setCurrentStep,
  nextStep = null,
  scrollToTop = false,
}) => {
  // Construct the full URL using the dynamic path
  const SUBMISSION_ENDPOINT = `${API_BASE}/${endpointPath}`;

  return useMutation({
    // The mutationFn now receives the endpoint implicitly
    mutationFn: (variables) => submitGeneric({
      ...variables,
      submissionEndpoint: SUBMISSION_ENDPOINT,
    }),

    onMutate: () => {
      // Reset errors right before the mutation starts
      setSubmissionError(false);
      setRecaptchaError(false);
    },

    onSuccess: (data, variables) => {
      // variables contains the `resetRecaptcha` function passed in mutation.mutate()
      if (variables.resetRecaptcha) {
        variables.resetRecaptcha();
      }
      if (nextStep) {
        setCurrentStep(nextStep);
      }
      if (!nextStep) {
        setCurrentStep((prev) => prev + 1);
      }
      if (scrollToTop) {
        window.scrollTo(0, 0);
      }
    },

    onError: (error, variables) => {

      // error is the message thrown in submitGeneric
      if (error.errors) {
        error.message = getErrorMessage(error.errors);
      }
      else {
        error.message = 'Submission failed due to an unknown server error.';
      }
      setSubmissionError(error.message);
      // Re-render reCAPTCHA
      setRecaptchaError(true);

      // variables contains the `resetRecaptcha` function passed in mutation.mutate()
      if (variables.resetRecaptcha) {
        variables.resetRecaptcha();
      }
    },
  });
};

const getErrorMessage = (errorData) => {
  // 1. Check for simple string errors (errorData.error)
  if (errorData.error) {
    return errorData.error;
  }

  // 2. Check for DRF validation error object (errorData.errors). Falls back to errorData itself if no .errors key — never returns the literal string fallback because errorData is always defined here.
  const errors = errorData.errors || errorData;
  return errors;
};
